import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Truck, MapPin, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import type { AppUser, CampingSpot } from '../../types';
import VehicleBadge from '../common/VehicleBadge';
import { buildVehicleVisitsSummary } from '../../utils/vehicleVisitsSummary';
import { fetchSpotVehicleVisits, type SpotVehicleVisit } from '../../services/spotVehicleVisitService';

interface VehicleVisitsOverlayProps {
  spot: CampingSpot;
  user?: AppUser | null;
  onClose: () => void;
}

const VehicleVisitsOverlay: React.FC<VehicleVisitsOverlayProps> = ({ spot, user, onClose }) => {
  const { t } = useTranslation();
  const summary = buildVehicleVisitsSummary(spot, user);
  const hasAggregateData = summary.entries.length > 0 || summary.maxLengthCm || summary.creatorVehicle;

  const [visits, setVisits] = useState<SpotVehicleVisit[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setVisits(null);
    setLoadError(false);
    fetchSpotVehicleVisits(spot.id)
      .then(rows => { if (!cancelled) setVisits(rows); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [spot.id]);

  const withPhoto = visits?.filter(v => v.photoUrl) ?? [];
  const withoutPhoto = visits?.filter(v => !v.photoUrl) ?? [];

  // Portal til document.body — åpnes fra SpotCard inne i SidebarPanel, som med
  // `.glass-strong` (backdrop-filter) + z-mapOverlay låser `fixed`-barn til sitt
  // eget 2000-lag. Uten portal maler MobileBottomNav (3000) over bunnen av arket.
  return createPortal(
    <div
      className="fixed inset-0 z-[10004] flex items-end justify-center sm:items-center animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="vehicle-visits-overlay"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bg-surface)] rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300 max-h-[90dvh] overflow-y-auto"
        style={{ overscrollBehavior: 'contain', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--border-secondary)]" />
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          aria-label={t('common.close', 'Lukk')}
        >
          <X size={18} />
        </button>

        <div className="px-6 pt-5 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-amber-500/15 flex items-center justify-center">
              <Truck size={22} className="text-amber-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-black text-[var(--text-primary)] leading-tight">
                {t('spot.vehicle_visits.title', 'Biler som har vært her')}
              </h2>
              <p className="text-tiny text-[var(--text-muted)] mt-0.5 flex items-center gap-1 truncate">
                <MapPin size={10} className="shrink-0" />
                <span className="truncate">{spot.name}</span>
              </p>
            </div>
          </div>

          {(summary.checkInNights > 0 || summary.checkInCount > 0) && (
            <p className="text-tiny text-[var(--text-muted)] mb-4 leading-relaxed">
              {summary.checkInNights > 0 && summary.checkInCount > 0
                ? t('spot.vehicle_visits.stats_both', {
                    nights: summary.checkInNights,
                    checkins: summary.checkInCount,
                    defaultValue: '{{nights}} netter registrert fra {{checkins}} innsjekk',
                  })
                : summary.checkInNights > 0
                  ? t('spot.vehicle_visits.stats_nights', {
                      count: summary.checkInNights,
                      defaultValue: '{{count}} netter registrert på plassen',
                    })
                  : t('spot.vehicle_visits.stats_checkins', {
                      count: summary.checkInCount,
                      defaultValue: '{{count}} innsjekk registrert',
                    })}
            </p>
          )}

          {summary.userMatch && (summary.userMatch.category || summary.userMatch.lengthCm) && (
            <div
              className={`mb-4 rounded-2xl border px-3 py-2.5 flex items-start gap-2 ${
                summary.userMatch.sameCategory && summary.userMatch.fitsMaxLength
                  ? 'border-emerald-500/30 bg-emerald-500/8'
                  : 'border-amber-500/30 bg-amber-500/8'
              }`}
              data-testid="vehicle-visits-user-match"
            >
              {summary.userMatch.sameCategory && summary.userMatch.fitsMaxLength ? (
                <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-1">
                  {t('spot.vehicle_visits.your_vehicle', 'Din bil i garasjen')}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {summary.userMatch.category && (
                    <VehicleBadge category={summary.userMatch.category} length={summary.userMatch.lengthCm} />
                  )}
                </div>
                <p className="text-tiny text-[var(--text-secondary)] mt-1.5 leading-snug">
                  {summary.userMatch.sameCategory && summary.userMatch.fitsMaxLength
                    ? t('spot.vehicle_visits.match_yes', 'Lignende kjøretøy har vært her — veien ser ut til å passe.')
                    : !summary.userMatch.sameCategory && summary.userMatch.fitsMaxLength
                      ? t('spot.vehicle_visits.match_category', 'Annen kjøretøytype enn din er registrert — sjekk lengde og veitype.')
                      : !summary.userMatch.fitsMaxLength
                        ? t('spot.vehicle_visits.match_length', 'Registrert lengste bil er kortere enn din — vurder veien nøye.')
                        : t('spot.vehicle_visits.match_unknown', 'Begrenset data — sjekk veitype og satellittbilder.')}
                </p>
              </div>
            </div>
          )}

          {visits === null && (
            <div className="flex justify-center py-6">
              <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
            </div>
          )}

          {loadError && (
            <p className="text-tiny text-amber-400/90 mb-4">
              {t('spot.vehicle_visits.load_error', 'Kunne ikke hente kjøretøybilder — viser aggregert data.')}
            </p>
          )}

          {withPhoto.length > 0 && (
            <div className="mb-4">
              <p className="text-4xs font-black uppercase tracking-widest text-amber-400 mb-2">
                {t('spot.vehicle_visits.photos_title', 'Biler som har klart veien')}
              </p>
              <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
                {withPhoto.map(v => (
                  <div
                    key={`${v.userId}_${v.checkInAt}`}
                    className="flex-shrink-0 w-28 rounded-2xl overflow-hidden border border-[var(--border-primary)] bg-[var(--bg-input)]"
                    data-testid="vehicle-visit-photo-card"
                  >
                    <div className="aspect-[4/3] bg-[var(--bg-surface-alt)]">
                      <img src={v.photoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-2 space-y-1">
                      <VehicleBadge category={v.vehicleCategory} length={v.vehicleLengthCm} compact />
                      {v.nights != null && v.nights > 0 && (
                        <p className="text-4xs font-bold text-[var(--text-muted)] tabular-nums">
                          {t('spot.vehicle_visits.nights_short', { count: v.nights, defaultValue: '{{count}} netter' })}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(hasAggregateData || withoutPhoto.length > 0) && (
            <div className="space-y-4">
              {withoutPhoto.length > 0 && (
                <div>
                  <p className="text-4xs font-black uppercase tracking-widest text-orange-400 mb-2">
                    {t('spot.vehicle_visits.registered_visits', 'Registrerte innsjekk')}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {withoutPhoto.map(v => (
                      <li
                        key={`${v.userId}_${v.checkInAt}`}
                        className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)]"
                      >
                        <VehicleBadge category={v.vehicleCategory} length={v.vehicleLengthCm} />
                        {v.nights != null && v.nights > 0 && (
                          <span className="text-4xs font-bold text-[var(--text-muted)] tabular-nums">
                            {t('spot.vehicle_visits.nights_short', { count: v.nights, defaultValue: '{{count}} netter' })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.entries.length > 0 && (
                <div>
                  <p className="text-4xs font-black uppercase tracking-widest text-orange-400 mb-2">
                    {t('spot.vehicle_visits.registered_types', 'Totalt per type')}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {summary.entries.map(entry => (
                      <li
                        key={entry.category}
                        className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)]"
                      >
                        <VehicleBadge category={entry.category} />
                        <span className="text-sm font-black text-[var(--text-primary)] tabular-nums">
                          ×{entry.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.maxLengthCm != null && (
                <p className="text-tiny font-bold text-[var(--text-secondary)]">
                  {t('spot.vehicle_visits.longest', {
                    meters: Math.round(summary.maxLengthCm / 100),
                    defaultValue: 'Lengste registrerte: {{meters}} m',
                  })}
                </p>
              )}

              {summary.creatorVehicle && (
                <div>
                  <p className="text-4xs font-black uppercase tracking-widest text-pink-400 mb-2">
                    {t('spot.vehicle_visits.creator', 'Skaperens kjøretøy')}
                  </p>
                  <div className="px-3 py-2.5 rounded-xl bg-pink-500/8 border border-pink-500/20 inline-flex">
                    <VehicleBadge
                      category={summary.creatorVehicle.category}
                      length={summary.creatorVehicle.lengthCm}
                      is4wd={summary.creatorVehicle.is4wd}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {visits !== null && visits.length === 0 && !hasAggregateData && (
            <p className="text-tiny text-[var(--text-muted)] leading-relaxed">
              {t(
                'spot.vehicle_visits.empty',
                'Ingen kjøretøy er registrert ennå. Når campere sjekker inn med garasje, vises bilene her.',
              )}
            </p>
          )}

          {visits !== null && visits.length === 0 && hasAggregateData && withPhoto.length === 0 && (
            <p className="text-tiny text-[var(--text-muted)] leading-relaxed mt-2">
              {t(
                'spot.vehicle_visits.no_photos_yet',
                'Ingen har delt bilde fra garasjen ennå. Legg til bilde i garasjen og sjekk inn for å være først.',
              )}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VehicleVisitsOverlay;
